import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { monitorAddress, getDetectedDeposits, stopMonitoringAddress } from '../api/debug';
import { useNotification } from './useNotification';
import { useTranslationWithNamespace } from './useTranslationWithNamespace';

// Define types for API responses
interface MonitorAddressResponse {
  status: string;
  message: string;
  data: {
    blockchain: string;
    address: string;
    coin: string;
  };
}

interface DetectedDepositsResponse {
  status: string;
  deposits: Array<{
    txid: string;
    amount: string;
    confirmations: number;
    timestamp: number;
    status: string;
  }>;
}

interface StopMonitoringResponse {
  status: string;
  message: string;
}

/**
 * Hook for monitoring an address
 * @returns Mutation for monitoring an address
 */
export const useMonitorAddress = () => {
  const queryClient = useQueryClient();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { t } = useTranslationWithNamespace('debug');

  return useMutation<
    MonitorAddressResponse,
    Error,
    { blockchain: string; address: string; coin?: string }
  >({
    mutationFn: (params: { blockchain: string; address: string; coin?: string }) => monitorAddress(params),
    onSuccess: (data: MonitorAddressResponse, variables: { blockchain: string; address: string; coin?: string }) => {
      showSuccessNotification(
        t('notifications.monitorAddressSuccess', 'Started monitoring address'),
        t('notifications.monitorAddressSuccessDesc', 'Now monitoring {address} on {blockchain}', {
          address: variables.address,
          blockchain: variables.blockchain
        })
      );
    },
    onError: (error: Error) => {
      showErrorNotification(
        t('notifications.monitorAddressError', 'Failed to monitor address'),
        error.message
      );
    }
  });
};

/**
 * Hook for stopping monitoring of an address
 * @returns Mutation for stopping monitoring of an address
 */
export const useStopMonitoringAddress = () => {
  const queryClient = useQueryClient();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { t } = useTranslationWithNamespace('debug');

  return useMutation<
    StopMonitoringResponse,
    Error,
    { blockchain: string; address: string }
  >({
    mutationFn: (params: { blockchain: string; address: string }) => stopMonitoringAddress(params),
    onSuccess: (data: StopMonitoringResponse, variables: { blockchain: string; address: string }) => {
      showSuccessNotification(
        t('notifications.stopMonitoringSuccess', 'Stopped monitoring address'),
        t('notifications.stopMonitoringSuccessDesc', 'No longer monitoring {address} on {blockchain}', {
          address: variables.address,
          blockchain: variables.blockchain
        })
      );
    },
    onError: (error: Error) => {
      showErrorNotification(
        t('notifications.stopMonitoringError', 'Failed to stop monitoring address'),
        error.message
      );
    }
  });
};

/**
 * Hook for fetching detected deposits
 * @param blockchain Blockchain name
 * @param address Address to check for deposits
 * @param enabled Whether to enable the query
 * @returns Query result with detected deposits
 */
export const useDetectedDeposits = (
  blockchain: string,
  address: string,
  enabled: boolean = true
) => {
  return useQuery<DetectedDepositsResponse>({
    queryKey: ['detected-deposits', blockchain, address],
    queryFn: () => getDetectedDeposits(blockchain, address),
    enabled: enabled && !!blockchain && !!address,
    refetchInterval: false, // We'll handle refetching manually
    staleTime: 0, // Always fetch fresh data
    retry: 1
  });
};
